/**
 * Lightweight event bus replacing the Vue 2 `new Vue()` pattern.
 *
 * Provides the same $on / $once / $off / $emit API that was previously
 * delegated to a bare Vue instance, without pulling in a full Vue app.
 *
 * @since KJS-Vue3-migration
 */
export default class EventBus {
  constructor() {
    this._listeners = new Map()
  }

  /**
   * Register a persistent listener for `event`.
   * Accepts a single event name or an array of event names.
   *
   * @param {string|string[]} event
   * @param {Function} handler
   * @returns {this}
   */
  $on(event, handler) {
    if (Array.isArray(event)) {
      for (const e of event) this.$on(e, handler)
      return this
    }
    if (!this._listeners.has(event)) {
      this._listeners.set(event, [])
    }
    this._listeners.get(event).push(handler)
    return this
  }

  /**
   * Register a one-time listener for `event`.
   * The listener is automatically removed after the first invocation.
   *
   * @param {string} event
   * @param {Function} handler
   * @returns {this}
   */
  $once(event, handler) {
    const wrapper = (...args) => {
      handler(...args)
      this.$off(event, wrapper)
    }
    wrapper._original = handler
    return this.$on(event, wrapper)
  }

  /**
   * Remove listeners.
   *
   * - No arguments  → remove all listeners for all events.
   * - Only `event`  → remove all listeners for that event.
   * - Both args       → remove that specific handler (also works for $once wrappers).
   * - Array of events  → applies the removal to each event in the array.
   *
   * @param {string|string[]} [event]
   * @param {Function} [handler]
   * @returns {this}
   */
  $off(event, handler) {
    if (Array.isArray(event)) {
      for (const e of event) this.$off(e, handler)
      return this
    }
    if (!event) {
      this._listeners.clear()
      return this
    }
    if (!handler) {
      this._listeners.delete(event)
      return this
    }
    const handlers = this._listeners.get(event)
    if (handlers) {
      const filtered = handlers.filter(
        h => h !== handler && h._original !== handler
      )
      if (filtered.length > 0) {
        this._listeners.set(event, filtered)
      } else {
        this._listeners.delete(event)
      }
    }
    return this
  }

  /**
   * Emit `event`, calling all registered listeners with the provided arguments.
   *
   * @param {string} event
   * @param {...*} args
   * @returns {this}
   */
  $emit(event, ...args) {
    const handlers = this._listeners.get(event)
    if (handlers) {
      // Shallow copy so that $once removals during iteration are safe
      for (const handler of [...handlers]) {
        handler(...args)
      }
    }
    return this
  }
}
